import React, { useState, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";

interface CollapsibleSidebarProps {
  children: (isCollapsed: boolean) => React.ReactNode;
  defaultCollapsed?: boolean;
}

const SIDEBAR_STATE_KEY = "sidebarCollapsed";

export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  children,
  defaultCollapsed = false,
}) => {
  // Detect if we're on mobile
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  // Initialize from localStorage if available
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return defaultCollapsed || isMobile;

    const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
    if (savedState !== null) {
      return savedState === "true";
    }
    return defaultCollapsed || isMobile;
  });

  // Update isMobile on window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-collapse on mobile, auto-expand on desktop
      if (mobile && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed]);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    // Persist to localStorage
    localStorage.setItem(SIDEBAR_STATE_KEY, String(newState));
  };

  return (
    <>
      {/* Backdrop for mobile when sidebar is open */}
      {isMobile && !isCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`bg-sidebar-gray min-h-[calc(100vh-4rem)] h-auto transition-all duration-300 ease-in-out border-r-2 border-border-gray ${
          isMobile
            ? `fixed left-0 top-16 bottom-0 z-50 ${isCollapsed ? "w-12" : "w-52"}`
            : `relative ${isCollapsed ? "w-12" : "w-52"}`
        }`}
        onClick={(e) => {
          // Prevent clicks on sidebar from doing anything
          e.stopPropagation();
        }}
      >
        {/* Toggle Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleSidebar();
          }}
          className="absolute -right-3 top-12 z-10 bg-white text-black border-2 border-border-gray hover:bg-gray-100 active:bg-gray-200 transition-colors w-6 h-6 flex items-center justify-center"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? (
            <ChevronRightIcon className="w-4 h-4" />
          ) : (
            <ChevronLeftIcon className="w-4 h-4" />
          )}
        </button>

        {/* Sidebar Content */}
        <nav className="pb-2 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
          <div onClick={(e) => e.stopPropagation()}>{children(isCollapsed)}</div>
        </nav>
      </aside>
    </>
  );
};
